// Types and Data for Schwartz Values survey
// Inspired by Shalom Schwartz's theory of basic human values - original implementation for GROWNi.sk

import { v4 as uuidv4 } from 'uuid'
import type { Timestamp } from '../firebase.ts'
import type { UserId } from '../ids.ts'
import { SurveyTypes } from '../Survey.ts'

export type SchwartzSurveyId = string

export interface SchwartzSurvey {
    id: SchwartzSurveyId
    surveyType: SurveyTypes.SCHWARTZ
    userId: UserId
    hash?: string
    questions: SchwartzQuestionType[]
    scores: SchwartzScores
    dominantValue: SchwartzValue
    secondaryValue?: SchwartzValue
    valueProfile: string // e.g., "Self-Enhancement" or "Self-Transcendence"
    confidence: number // 0-100
    isDraft?: boolean
    createdAt: Timestamp
    updatedAt?: Timestamp
    deletedAt?: Timestamp | null
}

// Schwartz's 10 basic values
export enum SchwartzValue {
    POWER = 'power', // Moc
    ACHIEVEMENT = 'achievement', // Výkon
    HEDONISM = 'hedonism', // Hedonizmus
    STIMULATION = 'stimulation', // Stimulácia
    SELF_DIRECTION = 'self_direction', // Sebariadenie
    UNIVERSALISM = 'universalism', // Univerzalizmus
    BENEVOLENCE = 'benevolence', // Benevolencia
    TRADITION = 'tradition', // Tradícia
    CONFORMITY = 'conformity', // Konformita
    SECURITY = 'security' // Bezpečnosť
}

export const SchwartzValueLabels: Record<SchwartzValue, string> = {
    [SchwartzValue.POWER]: 'Moc',
    [SchwartzValue.ACHIEVEMENT]: 'Výkon',
    [SchwartzValue.HEDONISM]: 'Hedonizmus',
    [SchwartzValue.STIMULATION]: 'Stimulácia',
    [SchwartzValue.SELF_DIRECTION]: 'Sebariadenie',
    [SchwartzValue.UNIVERSALISM]: 'Univerzalizmus',
    [SchwartzValue.BENEVOLENCE]: 'Benevolencia',
    [SchwartzValue.TRADITION]: 'Tradícia',
    [SchwartzValue.CONFORMITY]: 'Konformita',
    [SchwartzValue.SECURITY]: 'Bezpečnosť'
}

export const SchwartzValueDescriptions: Record<SchwartzValue, string> = {
    [SchwartzValue.POWER]: 'Sociálny status, prestíž a kontrola nad ľuďmi a zdrojmi',
    [SchwartzValue.ACHIEVEMENT]: 'Osobný úspech dosiahnutý prostredníctvom preukázania kompetencie',
    [SchwartzValue.HEDONISM]: 'Potešenie a zmyslové uspokojenie pre seba',
    [SchwartzValue.STIMULATION]: 'Vzrušenie, novosť a výzvy v živote',
    [SchwartzValue.SELF_DIRECTION]: 'Nezávislé myslenie a konanie, tvorivosť a sloboda',
    [SchwartzValue.UNIVERSALISM]: 'Pochopenie, oceňovanie a ochrana blaha všetkých ľudí a prírody',
    [SchwartzValue.BENEVOLENCE]: 'Zachovanie a zlepšenie blaha blízkych ľudí',
    [SchwartzValue.TRADITION]: 'Rešpekt a záväzok voči kultúrnym a náboženským zvykom',
    [SchwartzValue.CONFORMITY]: 'Obmedzenie akcií, ktoré by mohli ublížiť iným alebo porušiť sociálne normy',
    [SchwartzValue.SECURITY]: 'Bezpečnosť, harmónia a stabilita spoločnosti, vzťahov a seba'
}

// Question types: portrait (PVQ-style similarity rating) and scenario (forced choice)
// PVQ format is more engaging - describes a person and asks how similar you are
export type SchwartzQuestionFormat = 'portrait' | 'scenario'

export interface SchwartzQuestionType {
    id: string
    format: SchwartzQuestionFormat
    targetValue: SchwartzValue // Which value this question measures
    // For portrait questions (PVQ-style)
    portrait?: string // Description of a person who exemplifies this value
    // For scenario questions
    question?: string // The dilemma/situation description
    options?: SchwartzOption[]
    value?: number | string // 1-6 for portrait ("not like me" to "very much like me"), option ID for scenario
}

export interface SchwartzOption {
    id: string
    text: string
    value: SchwartzValue
}

export interface SchwartzScores {
    [SchwartzValue.POWER]: number
    [SchwartzValue.ACHIEVEMENT]: number
    [SchwartzValue.HEDONISM]: number
    [SchwartzValue.STIMULATION]: number
    [SchwartzValue.SELF_DIRECTION]: number
    [SchwartzValue.UNIVERSALISM]: number
    [SchwartzValue.BENEVOLENCE]: number
    [SchwartzValue.TRADITION]: number
    [SchwartzValue.CONFORMITY]: number
    [SchwartzValue.SECURITY]: number
}

// PVQ Scale: 1 = Vôbec nie som ako táto osoba, 6 = Som veľmi podobný/podobná tejto osobe
// 20 questions total - mix of PVQ portraits and vivid scenarios (2 per value)
export const SCHWARTZ_QUESTIONS: SchwartzQuestionType[] = [
    // POWER - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.POWER,
        portrait:
            'Tomáš je riaditeľ firmy. Je pre neho dôležité mať autoritu a rozhodovať o veľkých veciach. Rád ovplyvňuje, čo sa deje okolo neho, a chce, aby ho ostatní rešpektovali pre jeho pozíciu a vplyv.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.POWER,
        question:
            'Dostaneš ponuku na dve pracovné pozície s rovnakým platom. Prvá ti dá autoritu nad tímom 10 ľudí a rozhodovanie o firemnej stratégii. Druhá ti dá voľnosť pracovať na vlastných projektoch bez zodpovednosti za iných. Ktorú si vyberieš?',
        options: [
            { id: uuidv4(), text: 'Riadiaci pozícia - chcem rozhodovať a mať vplyv', value: SchwartzValue.POWER },
            {
                id: uuidv4(),
                text: 'Samostatná pozícia - chcem slobodu a vlastné tempo',
                value: SchwartzValue.SELF_DIRECTION
            },
            { id: uuidv4(), text: 'Záleží na tom, kde môžem viac pomôcť ľuďom', value: SchwartzValue.UNIVERSALISM }
        ]
    },

    // ACHIEVEMENT - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.ACHIEVEMENT,
        portrait:
            'Zuzana tvrdo pracuje na tom, aby bola najlepšia vo svojom odbore. Každý mesiac si nastavuje ciele a meria svoj pokrok. Keď ju pochváli šéf alebo kolega, je to pre ňu najväčšia odmena. Neuspokojí sa s priemerným výsledkom.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.ACHIEVEMENT,
        question:
            'Máš víkend voľný. Kamarát ťa pozýva na výlet k moru. Zároveň vieš, že keby si zostal/a doma a pracoval/a, mohol/a by si dokončiť projekt a získať povýšenie. Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Zostanem a budem pracovať - povýšenie je moja priorita',
                value: SchwartzValue.ACHIEVEMENT
            },
            { id: uuidv4(), text: 'Pôjdem na výlet - potrebujem si užiť a oddýchnuť', value: SchwartzValue.HEDONISM },
            {
                id: uuidv4(),
                text: 'Pôjdem, pretože pre kamaráta je to dôležité a chcem mu urobiť radosť',
                value: SchwartzValue.BENEVOLENCE
            }
        ]
    },

    // HEDONISM - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.HEDONISM,
        portrait:
            'Marek žije pre radosť. Rád si dopriava dobré jedlo, cestovanie a zábavu. Verí, že život je krátky a treba si ho užívať. Keď má na výber medzi prácou navyše a večerom s priateľmi, vždy si vyberie zábavu.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.HEDONISM,
        question:
            'Vyhrál/a si 5000€. Môžeš ich investovať do kurzu, ktorý ti zlepší kariéru. Alebo si môžeš kúpiť luxusnú dovolenku, o ktorej si vždy sníval/a. Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Dovolenka - zaslúžim si užiť si, kto vie, čo bude zajtra',
                value: SchwartzValue.HEDONISM
            },
            {
                id: uuidv4(),
                text: 'Investujem do vzdelania - dlhodobo sa mi to vráti',
                value: SchwartzValue.ACHIEVEMENT
            },
            { id: uuidv4(), text: 'Uložím ako rezervu - istota je prvoradá', value: SchwartzValue.SECURITY }
        ]
    },

    // STIMULATION - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.STIMULATION,
        portrait:
            'Petra neznáša rutinu. Vždy hľadá nové výzvy, dobrodružstvá a prekvapenia. Keď robí niečo prvýkrát, cíti sa nažive. Nudí ju predvídateľnosť a opakovanie tých istých vecí.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.STIMULATION,
        question:
            'Máš dva týždne dovolenky. Možnosť A: ísť do neznámej krajiny bez plánu a nechať sa prekvapiť. Možnosť B: tradičná rodinná chata pri jazere, kam chodíte každý rok. Čo si vyberieš?',
        options: [
            { id: uuidv4(), text: 'Neznáma krajina - dobrodružstvo a nové zážitky', value: SchwartzValue.STIMULATION },
            { id: uuidv4(), text: 'Chata - osvedčené miesto, bezpečnosť a pohodlie', value: SchwartzValue.SECURITY },
            {
                id: uuidv4(),
                text: 'Chata - tradícia je dôležitá, rodina tam chodí roky',
                value: SchwartzValue.TRADITION
            }
        ]
    },

    // SELF-DIRECTION - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.SELF_DIRECTION,
        portrait:
            'Lucia si najviac cení nezávislosť. Chce si sama určovať, čo bude robiť a ako. Je tvorivá a rada prichádza s vlastnými riešeniami. Neradi jej hovoria, čo má robiť - radšej si nájde cestu sama.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.SELF_DIRECTION,
        question:
            'V práci dostaneš úlohu. Šéf ti dá presný návod, ako ju spraviť. Ty máš lepší nápad, ale bude to trvať dlhšie. Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Spraví to po svojom - moja kreativita je dôležitá',
                value: SchwartzValue.SELF_DIRECTION
            },
            { id: uuidv4(), text: 'Nasledujem návod - pravidlá treba dodržiavať', value: SchwartzValue.CONFORMITY },
            {
                id: uuidv4(),
                text: 'Spravím to po svojom a presvedčím šéfa, že môj spôsob je lepší',
                value: SchwartzValue.POWER
            }
        ]
    },

    // UNIVERSALISM - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.UNIVERSALISM,
        portrait:
            'Adam sa stará o svet okolo seba. Recykluje, podporuje charitu a bojuje proti nespravodlivosti. Verí, že všetci ľudia si zaslúžia rovnaké príležitosti. Zaujíma ho blaho celej planéty, nielen jeho najbližších.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.UNIVERSALISM,
        question:
            'Zdedil/a si 10 000€. Tvoja sestra potrebuje peniaze na opravu domu. Zároveň vieš o organizácii, ktorá stavia studne v Afrike a zachráni stovky životov. Komu pomôžeš?',
        options: [
            {
                id: uuidv4(),
                text: 'Organizácii - zachránia viac životov, sestra si poradí',
                value: SchwartzValue.UNIVERSALISM
            },
            {
                id: uuidv4(),
                text: 'Sestre - rodina je na prvom mieste, blízki majú prednosť',
                value: SchwartzValue.BENEVOLENCE
            },
            {
                id: uuidv4(),
                text: 'Investujem do seba - silnejší ja môžem pomôcť viac v budúcnosti',
                value: SchwartzValue.ACHIEVEMENT
            }
        ]
    },

    // BENEVOLENCE - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.BENEVOLENCE,
        portrait:
            'Eva žije pre svoju rodinu a priateľov. Vždy nájde čas na blízkych, aj keď má sama veľa práce. Keď niekto z jej okolia potrebuje pomoc, je prvá pri telefóne. Jej šťastie závisí od šťastia jej blízkych.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.BENEVOLENCE,
        question:
            'Je piatok večer. Mal/a si ísť na koncert, na ktorý sa tešíš celé mesiace. Sestra ti zavolá, že ju opustil priateľ a potrebuje si porozprávať. Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Idem za sestrou - blízki majú prednosť pred zábavou',
                value: SchwartzValue.BENEVOLENCE
            },
            {
                id: uuidv4(),
                text: 'Idem na koncert - lístky boli drahé a chcem si to užiť',
                value: SchwartzValue.HEDONISM
            },
            {
                id: uuidv4(),
                text: 'Zavolám jej po koncerte - musím dodržať svoj plán',
                value: SchwartzValue.SELF_DIRECTION
            }
        ]
    },

    // TRADITION - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.TRADITION,
        portrait:
            'Ján rešpektuje tradície. Každé Vianoce robí rodine kapustnicu podľa receptu po babičke. Verí, že osvedčené zvyky majú hlbší zmysel a treba ich zachovávať. Moderné trendy ho často znepokojujú.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.TRADITION,
        question:
            'Tvoja rodina už 30 rokov trávi Vianoce u starých rodičov na dedine. Tento rok ti partner/ka navrhuje ísť na luxusný vianočný pobyt v Alpách. Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Idem k starým rodičom - tradícia je svätá', value: SchwartzValue.TRADITION },
            { id: uuidv4(), text: 'Idem do Álp - nový zážitok, nová tradícia', value: SchwartzValue.STIMULATION },
            { id: uuidv4(), text: 'Urobím kompromis, aby som nenarušil/a harmóniu', value: SchwartzValue.CONFORMITY }
        ]
    },

    // CONFORMITY - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.CONFORMITY,
        portrait:
            'Katka dbá na to, aby sa správala slušne. Nikdy nechce byť tá, čo narúša poriadok alebo prekračuje pravidlá. Verí, že spoločnosť funguje lepšie, keď všetci dodržiavajú normy. Nemá rada vyčnievať z davu.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.CONFORMITY,
        question:
            'Si na firemnej večeri. Všetci pijú alkohol, ale ty piť nechceš. Kolega ťa núti: "Čo si, divný/divná?". Čo urobíš?',
        options: [
            {
                id: uuidv4(),
                text: 'Dám si drink - nechcem vyčnievať a robiť problémy',
                value: SchwartzValue.CONFORMITY
            },
            {
                id: uuidv4(),
                text: 'Odmietnem - rozhodujem si sama, čo mi vyhovuje',
                value: SchwartzValue.SELF_DIRECTION
            },
            {
                id: uuidv4(),
                text: 'Odmietnem a vysvetlím všetkým, prečo je tlak na pitie zlý',
                value: SchwartzValue.POWER
            }
        ]
    },

    // SECURITY - 2 questions
    {
        id: uuidv4(),
        format: 'portrait',
        targetValue: SchwartzValue.SECURITY,
        portrait:
            'Martin potrebuje istotu. Má stálu prácu, sporí do fondu na dôchodok a plánuje všetko dopredu. Neistota ho stresuje - radšej vie, čo ho čaká, aj keď to nie je vzrušujúce. Stabilita je pre neho dôležitejšia ako dobrodružstvo.',
        value: undefined
    },
    {
        id: uuidv4(),
        format: 'scenario',
        targetValue: SchwartzValue.SECURITY,
        question:
            'Dostal/a si ponuku na vysnívanú prácu v startupu, ktorý môže byť veľký úspech, ale môže aj skrachovať. V súčasnej práci máš istotu, ale nudíš sa. Čo urobíš?',
        options: [
            { id: uuidv4(), text: 'Zostanem - istota je dôležitejšia ako sen', value: SchwartzValue.SECURITY },
            { id: uuidv4(), text: 'Skočím do startupu - chcem vzrušenie a výzvy', value: SchwartzValue.STIMULATION },
            { id: uuidv4(), text: 'Skočím - ak sa podarí, budem na vrchole', value: SchwartzValue.ACHIEVEMENT }
        ]
    }
]

// Fisher-Yates shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array]
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const left = shuffled[i]
        const right = shuffled[j]

        if (left === undefined || right === undefined) {
            continue
        }

        shuffled[i] = right
        shuffled[j] = left
    }
    return shuffled
}

// All 20 questions are used (2 per value type)
function getQuestionSet(): SchwartzQuestionType[] {
    // Shuffle to randomize order
    return shuffleArray(SCHWARTZ_QUESTIONS).map((q) => ({ ...q, value: undefined }))
}

// Initialize a new Schwartz survey
export function initializedSchwartzSurvey(userId: UserId): Omit<SchwartzSurvey, 'id' | 'createdAt'> {
    return {
        surveyType: SurveyTypes.SCHWARTZ,
        userId,
        hash: uuidv4(),
        questions: getQuestionSet(),
        scores: {
            [SchwartzValue.POWER]: 0,
            [SchwartzValue.ACHIEVEMENT]: 0,
            [SchwartzValue.HEDONISM]: 0,
            [SchwartzValue.STIMULATION]: 0,
            [SchwartzValue.SELF_DIRECTION]: 0,
            [SchwartzValue.UNIVERSALISM]: 0,
            [SchwartzValue.BENEVOLENCE]: 0,
            [SchwartzValue.TRADITION]: 0,
            [SchwartzValue.CONFORMITY]: 0,
            [SchwartzValue.SECURITY]: 0
        },
        dominantValue: SchwartzValue.SELF_DIRECTION,
        valueProfile: 'Balanced',
        confidence: 0,
        isDraft: true,
        deletedAt: null
    }
}
